// Iframe PostMessage Communication Handler
// This service manages communication between external iframes and the Jupiter dashboard

export interface IframeMessage {
  type: string;
  data: any;
  source?: string;
}

export interface ProjectNavigationMessage extends IframeMessage {
  type: 'NAVIGATE_TO_PROJECT';
  data: {
    // Core navigation data
    projectId: string | number;
    action?: 'details' | 'edit';
    context?: string;
    timestamp?: string; // ISO string timestamp

    // URL Parameters (tenant/user context)
    urlParams?: {
      tenant?: string;
      user?: string;
      system?: string;
      uid?: string;
      userID?: string;
    };

    // Complete project object from external source (e.g., calendar)
    // CRITICAL: All dates are now ISO strings for proper serialization
    project?: {
      id: number;
      title: string;
      projectId: number;
      refNumber: string;
      businessName: string;
      location: string;
      jobDescription: string;
      status: string;
      technicianStatus: string;
      contact: string;
      start: string; // ISO string, not Date object
      end: string;   // ISO string, not Date object
      technician: string;
      client: string;
      color: string;
    };

    // Structured project details for easy parent access
    projectDetails?: {
      // Core identification
      intProjectID: number;
      projectId: number;
      id: number;
      projectRefNumber: string;

      // Business information
      businessName: string;
      client: string;
      location: string;

      // Project details
      title: string;
      description: string;
      jobDescription: string;

      // Status and scheduling
      status: string;
      technicianStatus: string;
      technician: string;

      // Dates and timing - CRITICAL: All dates are ISO strings for proper serialization
      start: string;     // ISO string, not Date object
      end: string;       // ISO string, not Date object
      startDate: string; // ISO string, not Date object
      endDate: string;   // ISO string, not Date object
      dueDate: string;   // YYYY-MM-DD format

      // Contact and metadata
      contact: string;
      color: string;

      // Calendar context - CRITICAL: All dates are ISO strings
      viewMode?: string; // DAY/WEEK/MONTH/YEAR/TIMELINE
      selectedDate?: string; // ISO string, not Date object
      calendarContext?: {
        currentWeek?: string[]; // Array of ISO strings, not Date objects
        selectedMonth?: number; // 0-11 (September = 8)
        selectedYear?: number;
      };
    };

    // Legacy compatibility object - CRITICAL: All dates are ISO strings
    projectData?: {
      intProjectID: number;
      id: number;
      projectRefNumber: string;
      status: string;
      businessName: string;
      location: string;
      description: string;
      technician: string;
      client: string;
      start: string; // ISO string, not Date object
      end: string;   // ISO string, not Date object
      dueDate: string; // YYYY-MM-DD format
    };
  };
  source: 'calendar' | 'scheduler' | 'external';
}

class IframeMessageService {
  private eventListeners: Map<string, Function[]> = new Map();
  
  constructor() {
    this.setupMessageListener();
  }

  /**
   * Set up the global message listener for iframe communication
   */
  private setupMessageListener(): void {
    window.addEventListener('message', (event) => {

      // Security check - only allow messages from trusted origins
      const trustedOrigins = [
        'https://jupiter-scheduler-2023-v2.pages.dev',
        'https://calendar.snapsuite.io',   // New calendar production URL (without trailing slash)
        'https://calendar.snapsuite.io/',  // New calendar production URL (with trailing slash)
        'http://localhost:9693',  // Local development server
        'http://localhost:3000',  // Common local development port
        'http://localhost:8080',  // Another common local development port
        // Add other trusted calendar/scheduler domains here
      ];

      if (!trustedOrigins.includes(event.origin)) {
        return;
      }

      try {
        // Skip non-application messages (iframe lifecycle messages, etc.)
        if (typeof event.data === 'string') {

          // Skip iframe lifecycle messages and other non-JSON messages
          if (event.data.startsWith('IFRAME_') ||
            event.data.startsWith('NUXT_') ||
            event.data.length < 10) {
            return;
          }

          // Try to parse as JSON
          try {
            const message: IframeMessage = JSON.parse(event.data);

                // Only process messages with our expected structure
            if (message && typeof message === 'object' && message.type) {
                  this.handleMessage(message);
                }
          } catch (parseError) {
              return;
            }
          } else if (event.data && typeof event.data === 'object' && event.data.type) {
          // Handle object messages directly
          this.handleMessage(event.data as IframeMessage);
        }
      } catch (error) {
        // Handle errors silently in production
      }
    });
  }

  /**
   * Handle incoming messages from iframes
   */
  private handleMessage(message: IframeMessage): void {
    const listeners = this.eventListeners.get(message.type) || [];

    listeners.forEach((listener, index) => {
      try {
        listener(message);
      } catch (error) {
        // Handle listener errors silently in production
      }
    });
  }

  /**
   * Register a listener for specific message types
   */
  public on(messageType: string, callback: Function): void {
    if (!this.eventListeners.has(messageType)) {
      this.eventListeners.set(messageType, []);
    }
    
    this.eventListeners.get(messageType)!.push(callback);
  }

  /**
   * Remove a listener for specific message types
   */
  public off(messageType: string, callback: Function): void {
    const listeners = this.eventListeners.get(messageType) || [];
    const index = listeners.indexOf(callback);
    
    if (index > -1) {
      listeners.splice(index, 1);
    }
  }

  /**
   * Send a message to an iframe
   */
  public sendToIframe(iframe: HTMLIFrameElement, message: IframeMessage): void {
    if (iframe.contentWindow) {
      iframe.contentWindow.postMessage(message, '*');
    }
  }

  /**
   * Send acknowledgment back to iframe
   */
  public sendAcknowledgment(targetOrigin: string, originalMessage: IframeMessage): void {
    const ackMessage: IframeMessage = {
      type: 'ACKNOWLEDGMENT',
      data: {
        originalType: originalMessage.type,
        status: 'success',
        timestamp: new Date().toISOString()
      }
    };

    window.parent.postMessage(ackMessage, targetOrigin);
  }
}

// Export singleton instance
export const iframeMessageService = new IframeMessageService();
export default iframeMessageService;
